'use client';

import { MfeReactComponent } from '@carvana/microfrontend-react';
import { useCallback } from 'react';
import { COMPOSER_MODULE } from '~/constants/common/MfeModules';
import {
  useComposerExperiments,
  useMarkComposerComponentMounted,
  useRevertComposerTreatment,
  useShowComposerTreatment
} from '~/context/composer/ComposerContext';
import useLoadComposerUiMfe, { logComposerError } from './useLoadComposerUiMfe';

const COMPOSER_MFE_TIMEOUT_MS = 10000;
const COMPOSER_MFE_FALLBACK = {
  Component: <></>,
  timeout: COMPOSER_MFE_TIMEOUT_MS
};

export default function ComposerUiMfe() {
  const experiments = useComposerExperiments();
  const markComponentMounted = useMarkComposerComponentMounted();
  const revertToControl = useRevertComposerTreatment();
  const showComposerTreatment = useShowComposerTreatment();

  useLoadComposerUiMfe();

  const handleComponentLoaded = useCallback(() => {
    markComponentMounted();
  }, [markComponentMounted]);

  const handleComponentError = useCallback(
    ({ error }: { error?: Error }) => {
      try {
        logComposerError(error ?? new Error('Composer UI MFE render error'));
      } catch {
        // Never let a logging failure escalate — this runs inside the vendor's
        // componentDidCatch, where an uncaught throw here would propagate past
        // Composer's own error boundary and take down the whole page.
      }

      revertToControl('component-error');
    },
    [revertToControl]
  );

  const handleComponentTimeout = useCallback(() => {
    logComposerError(
      new Error('Composer UI MFE failed to load within timeout')
    );
    revertToControl('component-timeout');
  }, [revertToControl]);

  if (!showComposerTreatment) return null;

  return (
    <MfeReactComponent
      component="default"
      experiments={experiments}
      module={COMPOSER_MODULE}
      fallback={COMPOSER_MFE_FALLBACK}
      onComponentError={handleComponentError}
      onComponentLoaded={handleComponentLoaded}
      onComponentTimeout={handleComponentTimeout}
    />
  );
}
